'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Brain, Zap, CheckCircle, XCircle, RefreshCw, ChevronRight, Trophy, Clock, Star, Flame, Target, BookOpen, Sparkles, Settings, SlidersHorizontal, X } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { useSession } from './SessionProvider';
import { dispatchQuizComplete } from './LeaderboardStreakPanel';
import { quizService } from '@/lib/services/quizService';

// ─── Types ────────────────────────────────────────────────────────────────────
interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

interface ParsedQuiz {
  questions: QuizQuestion[];
  sessionContext: string;
}

type QuizPhase = 'idle' | 'generating' | 'active' | 'review' | 'complete';
type DifficultyMode = 'auto' | 'easy' | 'medium' | 'hard';

// ─── Quiz Settings Types ──────────────────────────────────────────────────────
interface QuizSettings {
  difficultyMode: DifficultyMode;
  questionTypes: string[];
  calibrationBias: number; // -2 to +2: negative = easier, positive = harder
  questionCount: number;
}

const QUESTION_TYPE_OPTIONS = [
  { id: 'working_memory', label: 'Working Memory', emoji: '🧩', desc: 'Short-term retention & manipulation' },
  { id: 'pattern_recognition', label: 'Pattern Recognition', emoji: '🔍', desc: 'Visual & logical patterns' },
  { id: 'attention', label: 'Attention & Focus', emoji: '🎯', desc: 'Selective & sustained attention' },
  { id: 'executive_function', label: 'Executive Function', emoji: '⚙️', desc: 'Planning, inhibition, flexibility' },
  { id: 'neuroscience', label: 'Neuroscience Facts', emoji: '🧬', desc: 'Brain science & EEG knowledge' },
  { id: 'cognitive_psychology', label: 'Cognitive Psychology', emoji: '💭', desc: 'Mental processes & behaviour' },
];

const DEFAULT_SETTINGS: QuizSettings = {
  difficultyMode: 'auto',
  questionTypes: QUESTION_TYPE_OPTIONS.map(t => t.id),
  calibrationBias: 0,
  questionCount: 3,
};

// ─── Brain Facts for idle state ───────────────────────────────────────────────
const BRAIN_FACTS = [
  { emoji: '⚡', fact: 'Your brain generates about 23 watts of power — enough to light a small LED bulb!' },
  { emoji: '🔗', fact: 'The brain has ~86 billion neurons, each connected to up to 10,000 others.' },
  { emoji: '🌊', fact: 'Alpha waves (8–13 Hz) appear when you close your eyes and relax — your brain\'s "idle mode".' },
  { emoji: '🎯', fact: 'Theta waves (4–8 Hz) peak during deep focus and creative flow states.' },
  { emoji: '💡', fact: 'Regular cognitive challenges physically thicken your prefrontal cortex.' },
  { emoji: '🧬', fact: 'Neuroplasticity means your brain rewires itself every time you learn something new.' },
];

// ─── Motivational messages ────────────────────────────────────────────────────
const CORRECT_MSGS = ['🎉 Brilliant!', '🔥 On Fire!', '⚡ Supercharged!', '🧠 Neural Master!', '✨ Genius Move!'];
const WRONG_MSGS = ['💪 Almost!', '🔄 Keep Going!', '🌱 Learning!', '📚 Good Try!'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCognitiveProfile(
  cognitiveLoad: number,
  attentionScore: number,
  thetaAlphaRatio: number,
  betaPower: number,
  alphaPower: number,
  thetaPower: number
): { label: string; difficulty: string; focus: string; emoji: string } {
  const avgLoad = (cognitiveLoad + attentionScore) / 2;
  if (avgLoad >= 70 && thetaAlphaRatio < 1.4) {
    return { label: 'Peak Focus', difficulty: 'hard', focus: 'advanced reasoning and working memory', emoji: '🚀' };
  }
  if (avgLoad >= 50) {
    return { label: 'Engaged', difficulty: 'medium', focus: 'pattern recognition and logical deduction', emoji: '⚡' };
  }
  if (thetaAlphaRatio > 1.6) {
    return { label: 'Fatigued', difficulty: 'easy', focus: 'recall and simple associations', emoji: '😴' };
  }
  return { label: 'Relaxed', difficulty: 'easy', focus: 'creative thinking and broad associations', emoji: '🌊' };
}

function getEffectiveDifficulty(
  settings: QuizSettings,
  autoDifficulty: string
): string {
  if (settings.difficultyMode !== 'auto') return settings.difficultyMode;
  // Apply calibration bias to auto difficulty
  const levels = ['easy', 'medium', 'hard'];
  const baseIdx = levels.indexOf(autoDifficulty);
  const biasedIdx = Math.max(0, Math.min(2, baseIdx + Math.round(settings.calibrationBias)));
  return levels[biasedIdx];
}

function buildQuizPrompt(
  cognitiveLoad: number,
  attentionScore: number,
  thetaAlphaRatio: number,
  betaPower: number,
  alphaPower: number,
  thetaPower: number,
  coherence: number,
  settings: QuizSettings
): string {
  const autoProfile = getCognitiveProfile(cognitiveLoad, attentionScore, thetaAlphaRatio, betaPower, alphaPower, thetaPower);
  const effectiveDifficulty = getEffectiveDifficulty(settings, autoProfile.difficulty);

  const activeTypes = settings.questionTypes.length > 0
    ? QUESTION_TYPE_OPTIONS.filter(t => settings.questionTypes.includes(t.id)).map(t => t.label)
    : QUESTION_TYPE_OPTIONS.map(t => t.label);

  const calibrationNote = settings.calibrationBias !== 0
    ? `\n- Score Calibration: ${settings.calibrationBias > 0 ? `+${settings.calibrationBias} (user prefers harder challenges)` : `${settings.calibrationBias} (user prefers easier challenges)`}`
    : '';

  const focusNote = settings.difficultyMode === 'auto'
    ? autoProfile.focus
    : effectiveDifficulty === 'hard' ? 'advanced reasoning, complex problem-solving, and deep cognitive challenges'
    : effectiveDifficulty === 'medium' ? 'pattern recognition, logical deduction, and moderate cognitive effort'
    : 'recall, simple associations, and foundational brain health concepts';

  return `You are a cognitive neuroscience expert designing a personalized brain health quiz.

Current EEG Session Metrics:
- Cognitive Load: ${cognitiveLoad.toFixed(0)}/100
- Attention Score: ${attentionScore.toFixed(0)}/100
- Theta/Alpha Ratio: ${thetaAlphaRatio.toFixed(2)} (fatigue indicator)
- Beta Power: ${betaPower.toFixed(1)} µV² (active cognition)
- Alpha Power: ${alphaPower.toFixed(1)} µV² (relaxed awareness)
- Theta Power: ${thetaPower.toFixed(1)} µV² (memory/drowsiness)
- Inter-Hemispheric Coherence: ${coherence.toFixed(0)}%
- Cognitive Profile: ${autoProfile.label}${calibrationNote}

User Settings:
- Difficulty: ${settings.difficultyMode === 'auto' ? `Auto-calibrated → ${effectiveDifficulty}` : effectiveDifficulty}
- Preferred Question Types: ${activeTypes.join(', ')}

Generate exactly ${settings.questionCount} multiple-choice cognitive challenge questions tailored to this user's current brain state (${autoProfile.label} — focus on ${focusNote}).

IMPORTANT: Respond ONLY with valid JSON in this exact format, no markdown, no extra text:
{
  "sessionContext": "One sentence describing the user's current cognitive state and why these questions were chosen.",
  "questions": [
    {
      "question": "Question text here?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctIndex": 0,
      "explanation": "Brief neuroscience-grounded explanation of why this is correct and what it means for brain health.",
      "difficulty": "${effectiveDifficulty}",
      "category": "Category name — must be one of: ${activeTypes.join(', ')}"
    }
  ]
}

Make questions genuinely challenging and educational. Vary the categories across the ${settings.questionCount} questions, choosing only from the allowed categories above.`;
}

// ─── Confetti Particle ────────────────────────────────────────────────────────
function ConfettiParticle({ delay, color }: { delay: number; color: string }) {
  return (
    <div
      className="absolute w-2 h-2 rounded-sm pointer-events-none"
      style={{
        background: color,
        left: `${Math.random() * 100}%`,
        top: '-8px',
        animation: `confettiFall 1.2s ${delay}s ease-in forwards`,
        transform: `rotate(${Math.random() * 360}deg)`,
      }}
    />
  );
}

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, total }: { score: number; total: number }) {
  const pct = total > 0 ? score / total : 0;
  const r = 32;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const color = pct >= 0.8 ? '#22d3ee' : pct >= 0.5 ? '#F97316' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
      <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(30,58,95,0.4)" strokeWidth="6" />
        <circle
          cx="40" cy="40" r={r} fill="none"
          stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.34,1.56,0.64,1)', filter: `drop-shadow(0 0 8px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-white leading-none">{score}</span>
        <span className="text-[11px] text-[#4A7AB5]">/{total}</span>
      </div>
    </div>
  );
}

// ─── Difficulty Badge ─────────────────────────────────────────────────────────
function DifficultyBadge({ difficulty }: { difficulty: string }) {
  const map: Record<string, { color: string; bg: string; emoji: string }> = {
    easy: { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', emoji: '🟢' },
    medium: { color: '#F97316', bg: 'rgba(249,115,22,0.1)', emoji: '🟡' },
    hard: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', emoji: '🔴' },
  };
  const style = map[difficulty] || map.medium;
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ color: style.color, background: style.bg, border: `1px solid ${style.color}30` }}
    >
      {style.emoji} {difficulty}
    </span>
  );
}

// ─── XP Bar ───────────────────────────────────────────────────────────────────
function XPBar({ xp, maxXp }: { xp: number; maxXp: number }) {
  const pct = Math.min((xp / maxXp) * 100, 100);
  return (
    <div className="flex items-center gap-2">
      <Star className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
      <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(30,58,95,0.4)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #F97316, #FFD700)',
            boxShadow: '0 0 6px rgba(249,115,22,0.5)',
          }}
        />
      </div>
      <span className="text-[10px] text-yellow-400 font-bold">{xp} XP</span>
    </div>
  );
}

// ─── Quiz Settings Panel ──────────────────────────────────────────────────────
function QuizSettingsPanel({
  settings,
  onChange,
  onClose,
  autoDifficulty,
}: {
  settings: QuizSettings;
  onChange: (s: QuizSettings) => void;
  onClose: () => void;
  autoDifficulty: string;
}) {
  const difficultyOptions: { value: DifficultyMode; label: string; emoji: string; desc: string }[] = [
    { value: 'auto', label: 'Auto', emoji: '🧠', desc: `EEG-calibrated (currently: ${autoDifficulty})` },
    { value: 'easy', label: 'Easy', emoji: '🟢', desc: 'Recall & foundational concepts' },
    { value: 'medium', label: 'Medium', emoji: '🟡', desc: 'Pattern recognition & logic' },
    { value: 'hard', label: 'Hard', emoji: '🔴', desc: 'Advanced reasoning & deep science' },
  ];

  const biasLabels: Record<number, string> = {
    '-2': 'Much Easier',
    '-1': 'Slightly Easier',
    '0': 'Balanced',
    '1': 'Slightly Harder',
    '2': 'Much Harder',
  };

  const toggleType = (id: string) => {
    const current = settings.questionTypes;
    if (current.includes(id)) {
      if (current.length === 1) return; // keep at least one
      onChange({ ...settings, questionTypes: current.filter(t => t !== id) });
    } else {
      onChange({ ...settings, questionTypes: [...current, id] });
    }
  };

  const allSelected = settings.questionTypes.length === QUESTION_TYPE_OPTIONS.length;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.98) 0%, rgba(6,13,26,1) 100%)',
        border: '1px solid rgba(34,211,238,0.25)',
        boxShadow: '0 0 60px rgba(34,211,238,0.07), 0 8px 32px rgba(0,0,0,0.5)',
        animation: 'settingsReveal 0.3s cubic-bezier(0.16,1,0.3,1) both',
      }}
    >
      {/* Top accent */}
      <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #22d3ee 30%, #a855f7 50%, #22d3ee 70%, transparent)' }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.25)' }}
            >
              <SlidersHorizontal className="w-4 h-4 text-[#22d3ee]" />
            </div>
            <div>
              <h4 className="text-[14px] font-black text-white">Quiz Settings</h4>
              <p className="text-[10px] text-[#4A7AB5]">Personalise your cognitive challenge</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200"
            style={{ background: 'rgba(30,58,95,0.3)', border: '1px solid rgba(30,58,95,0.5)' }}
          >
            <X className="w-3.5 h-3.5 text-[#4A7AB5]" />
          </button>
        </div>

        {/* ── Section 1: Difficulty ── */}
        <div className="mb-5">
          <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-black mb-3 flex items-center gap-1.5">
            <Brain className="w-3 h-3" /> Difficulty Mode
          </p>
          <div className="grid grid-cols-2 gap-2">
            {difficultyOptions.map(opt => {
              const isActive = settings.difficultyMode === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => onChange({ ...settings, difficultyMode: opt.value })}
                  className="rounded-xl p-3 text-left transition-all duration-200"
                  style={{
                    background: isActive ? 'rgba(34,211,238,0.1)' : 'rgba(30,58,95,0.1)',
                    border: `1px solid ${isActive ? 'rgba(34,211,238,0.4)' : 'rgba(30,58,95,0.3)'}`,
                    boxShadow: isActive ? '0 0 12px rgba(34,211,238,0.1)' : 'none',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm">{opt.emoji}</span>
                    <span className="text-[12px] font-black" style={{ color: isActive ? '#22d3ee' : '#8BAFD4' }}>{opt.label}</span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#22d3ee]" style={{ boxShadow: '0 0 4px #22d3ee' }} />
                    )}
                  </div>
                  <p className="text-[9px] leading-tight" style={{ color: isActive ? '#22d3ee99' : '#4A7AB5' }}>{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Section 2: Score Calibration (only when Auto) ── */}
        {settings.difficultyMode === 'auto' && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-black flex items-center gap-1.5">
                <Target className="w-3 h-3" /> Score Calibration
              </p>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  color: settings.calibrationBias > 0 ? '#ef4444' : settings.calibrationBias < 0 ? '#22d3ee' : '#F97316',
                  background: settings.calibrationBias > 0 ? 'rgba(239,68,68,0.1)' : settings.calibrationBias < 0 ? 'rgba(34,211,238,0.1)' : 'rgba(249,115,22,0.1)',
                  border: `1px solid ${settings.calibrationBias > 0 ? 'rgba(239,68,68,0.3)' : settings.calibrationBias < 0 ? 'rgba(34,211,238,0.3)' : 'rgba(249,115,22,0.3)'}`,
                }}
              >
                {biasLabels[settings.calibrationBias] ?? 'Balanced'}
              </span>
            </div>
            <p className="text-[10px] text-[#4A7AB5] mb-3 leading-relaxed">
              Shift the EEG-derived difficulty up or down based on how you feel this session.
            </p>
            <div className="relative px-1">
              <input
                type="range"
                min={-2}
                max={2}
                step={1}
                value={settings.calibrationBias}
                onChange={e => onChange({ ...settings, calibrationBias: Number(e.target.value) })}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(90deg, #22d3ee ${((settings.calibrationBias + 2) / 4) * 100}%, rgba(30,58,95,0.4) ${((settings.calibrationBias + 2) / 4) * 100}%)`,
                  accentColor: '#22d3ee',
                }}
              />
              <div className="flex justify-between mt-1.5">
                {['−2', '−1', '0', '+1', '+2'].map(v => (
                  <span key={v} className="text-[9px] text-[#4A7AB5] font-semibold">{v}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Section 3: Question Count ── */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-black flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Questions per Session
            </p>
            <span
              className="text-[11px] font-black px-2.5 py-0.5 rounded-full"
              style={{ color: '#F97316', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)' }}
            >
              {settings.questionCount}
            </span>
          </div>
          <div className="flex gap-2">
            {[3, 5, 7].map(n => {
              const isActive = settings.questionCount === n;
              return (
                <button
                  key={n}
                  onClick={() => onChange({ ...settings, questionCount: n })}
                  className="flex-1 rounded-xl py-2.5 text-[12px] font-black transition-all duration-200"
                  style={{
                    background: isActive ? 'rgba(249,115,22,0.15)' : 'rgba(30,58,95,0.1)',
                    border: `1px solid ${isActive ? 'rgba(249,115,22,0.4)' : 'rgba(30,58,95,0.3)'}`,
                    color: isActive ? '#F97316' : '#4A7AB5',
                    boxShadow: isActive ? '0 0 10px rgba(249,115,22,0.1)' : 'none',
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Section 4: Question Type Filters ── */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-black flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" /> Question Types
            </p>
            <button
              onClick={() => onChange({
                ...settings,
                questionTypes: allSelected ? [QUESTION_TYPE_OPTIONS[0].id] : QUESTION_TYPE_OPTIONS.map(t => t.id),
              })}
              className="text-[9px] font-bold px-2 py-0.5 rounded-full transition-all duration-200"
              style={{ color: '#4A7AB5', background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.3)' }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="space-y-1.5">
            {QUESTION_TYPE_OPTIONS.map(type => {
              const isActive = settings.questionTypes.includes(type.id);
              return (
                <button
                  key={type.id}
                  onClick={() => toggleType(type.id)}
                  className="w-full rounded-xl px-3 py-2.5 flex items-center gap-3 transition-all duration-200 text-left"
                  style={{
                    background: isActive ? 'rgba(34,211,238,0.06)' : 'rgba(30,58,95,0.06)',
                    border: `1px solid ${isActive ? 'rgba(34,211,238,0.25)' : 'rgba(30,58,95,0.25)'}`,
                  }}
                >
                  <span className="text-base flex-shrink-0">{type.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold" style={{ color: isActive ? '#22d3ee' : '#8BAFD4' }}>{type.label}</p>
                    <p className="text-[9px] truncate" style={{ color: isActive ? 'rgba(34,211,238,0.6)' : '#4A7AB5' }}>{type.desc}</p>
                  </div>
                  <div
                    className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-200"
                    style={{
                      background: isActive ? '#22d3ee' : 'rgba(30,58,95,0.3)',
                      border: `1px solid ${isActive ? '#22d3ee' : 'rgba(30,58,95,0.5)'}`,
                    }}
                  >
                    {isActive && (
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 3L3 5L7 1" stroke="rgba(6,13,26,1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reset button */}
        <button
          onClick={() => onChange(DEFAULT_SETTINGS)}
          className="w-full rounded-xl py-2.5 text-[11px] font-semibold text-[#4A7AB5] transition-all duration-200 flex items-center justify-center gap-1.5"
          style={{ background: 'rgba(30,58,95,0.1)', border: '1px solid rgba(30,58,95,0.25)' }}
        >
          <RefreshCw className="w-3 h-3" />
          Reset to Defaults
        </button>
      </div>

      <style>{`
        @keyframes settingsReveal {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BrainHealthQuiz() {
  const { metrics } = useSession();
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  const [phase, setPhase] = useState<QuizPhase>('idle');
  const [quiz, setQuiz] = useState<ParsedQuiz | null>(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [totalXP, setTotalXP] = useState(0);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [factIndex, setFactIndex] = useState(0);
  const [answerFlash, setAnswerFlash] = useState<'correct' | 'wrong' | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [quizSettings, setQuizSettings] = useState<QuizSettings>(DEFAULT_SETTINGS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prevLoadingRef = useRef(false);

  useEffect(() => {
    setMounted(true);
    setFactIndex(Math.floor(Math.random() * BRAIN_FACTS.length));
  }, []);

  // Rotate brain facts
  useEffect(() => {
    if (phase !== 'idle') return;
    const interval = setInterval(() => {
      setFactIndex(i => (i + 1) % BRAIN_FACTS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [phase]);

  // Parse quiz from AI response
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading && response && phase === 'generating') {
      try {
        const cleaned = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        const parsed: ParsedQuiz = JSON.parse(cleaned);
        if (parsed.questions && parsed.questions.length > 0) {
          setQuiz(parsed);
          setAnswers(new Array(parsed.questions.length).fill(null));
          setCurrentQ(0);
          setScore(0);
          setStreak(0);
          setSelectedAnswer(null);
          setShowExplanation(false);
          setPhase('active');
          setTimeLeft(30);
          setTimerActive(true);
        } else {
          setPhase('idle');
        }
      } catch {
        setPhase('idle');
      }
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, response, phase]);

  // Countdown timer
  useEffect(() => {
    if (!timerActive || phase !== 'active') return;
    if (timeLeft <= 0) {
      handleTimeUp();
      return;
    }
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [timeLeft, timerActive, phase]);

  const handleTimeUp = useCallback(() => {
    setTimerActive(false);
    if (selectedAnswer === null) {
      setSelectedAnswer(-1);
      setShowExplanation(true);
      setStreak(0);
      setFeedbackMsg('⏰ Time\'s up!');
    }
  }, [selectedAnswer]);

  const generateQuiz = useCallback(() => {
    if (!metrics) return;
    setPhase('generating');
    setShowSettings(false);
    const prompt = buildQuizPrompt(
      metrics.cognitiveLoadIndex,
      metrics.attentionScore,
      metrics.thetaAlphaRatio,
      metrics.betaPower,
      metrics.alphaPower,
      metrics.thetaPower,
      metrics.interHemisphericCoherence,
      quizSettings
    );
    sendMessage(
      [
        { role: 'system', content: 'You are a cognitive neuroscience expert. Always respond with valid JSON only, no markdown formatting.' },
        { role: 'user', content: prompt },
      ],
      { max_completion_tokens: 1800 }
    );
  }, [metrics, sendMessage, quizSettings]);

  const handleSelectAnswer = useCallback((idx: number) => {
    if (selectedAnswer !== null || !quiz) return;
    setTimerActive(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    setSelectedAnswer(idx);
    setShowExplanation(true);
    const correct = quiz.questions[currentQ].correctIndex === idx;

    if (correct) {
      setScore(s => s + 1);
      setStreak(s => s + 1);
      const xpGain = quiz.questions[currentQ].difficulty === 'hard' ? 30 : quiz.questions[currentQ].difficulty === 'medium' ? 20 : 10;
      setTotalXP(x => x + xpGain + (timeLeft > 20 ? 10 : timeLeft > 10 ? 5 : 0));
      setFeedbackMsg(CORRECT_MSGS[Math.floor(Math.random() * CORRECT_MSGS.length)]);
      setAnswerFlash('correct');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1500);
    } else {
      setStreak(0);
      setFeedbackMsg(WRONG_MSGS[Math.floor(Math.random() * WRONG_MSGS.length)]);
      setAnswerFlash('wrong');
    }
    setTimeout(() => setAnswerFlash(null), 600);

    const newAnswers = [...answers];
    newAnswers[currentQ] = idx;
    setAnswers(newAnswers);
  }, [selectedAnswer, quiz, currentQ, answers, timeLeft]);

  const handleNext = useCallback(() => {
    if (!quiz) return;
    if (currentQ < quiz.questions.length - 1) {
      setCurrentQ(q => q + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setTimeLeft(30);
      setTimerActive(true);
      setFeedbackMsg('');
    } else {
      setPhase('complete');
      setTimerActive(false);
      // Dispatch quiz completion for leaderboard/streak tracking
      const autoProfile = metrics
        ? getCognitiveProfile(
            metrics.cognitiveLoadIndex,
            metrics.attentionScore,
            metrics.thetaAlphaRatio,
            metrics.betaPower,
            metrics.alphaPower,
            metrics.thetaPower
          )
        : null;
      const diff = autoProfile
        ? getEffectiveDifficulty(quizSettings, autoProfile.difficulty)
        : 'medium';
      dispatchQuizComplete(score, quiz.questions.length, diff);

      // Persist to Supabase
      const xpEarned = answers.reduce((acc, ans, i) => {
        if (ans === quiz.questions[i]?.correctIndex) {
          return acc + (quiz.questions[i].difficulty === 'hard' ? 30 : quiz.questions[i].difficulty === 'medium' ? 20 : 10);
        }
        return acc;
      }, 0);
      quizService.saveQuizSession({
        score,
        total: quiz.questions.length,
        xp: xpEarned,
        difficulty: diff,
      }).catch(() => {/* non-critical */});
    }
  }, [quiz, currentQ, score, metrics, quizSettings, answers]);

  const handleRestart = useCallback(() => {
    setPhase('idle');
    setQuiz(null);
    setCurrentQ(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setStreak(0);
    setTimerActive(false);
    setFeedbackMsg('');
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  if (!mounted) return null;

  const profile = metrics
    ? getCognitiveProfile(
        metrics.cognitiveLoadIndex,
        metrics.attentionScore,
        metrics.thetaAlphaRatio,
        metrics.betaPower,
        metrics.alphaPower,
        metrics.thetaPower
      )
    : null;

  const effectiveDifficulty = profile
    ? getEffectiveDifficulty(quizSettings, profile.difficulty)
    : quizSettings.difficultyMode !== 'auto'
      ? quizSettings.difficultyMode
      : 'medium';

  // ── Settings Panel (shown over idle) ────────────────────────────────────────
  if (showSettings) {
    return (
      <QuizSettingsPanel
        settings={quizSettings}
        onChange={setQuizSettings}
        onClose={() => setShowSettings(false)}
        autoDifficulty={profile?.difficulty ?? 'medium'}
      />
    );
  }

  // ── Idle State ──────────────────────────────────────────────────────────────
  if (phase === 'idle') {
    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(11,22,40,0.97) 0%, rgba(6,13,26,0.99) 100%)',
          border: '1px solid rgba(249,115,22,0.25)',
          boxShadow: '0 0 60px rgba(249,115,22,0.07), 0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        {/* Animated top accent */}
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #F97316 30%, #FFD700 50%, #F97316 70%, transparent)', animation: 'accentShimmer 3s ease-in-out infinite' }} />

        <div className="p-6">
          {/* Title row */}
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(249,115,22,0.2) 0%, rgba(249,115,22,0.05) 100%)',
                border: '1px solid rgba(249,115,22,0.35)',
                boxShadow: '0 0 16px rgba(249,115,22,0.15)',
              }}
            >
              <span className="text-xl">🧠</span>
            </div>
            <div className="flex-1">
              <h3 className="text-[16px] font-black text-white tracking-wide">Brain Health Quiz</h3>
              <p className="text-[11px] text-[#F97316] mt-0.5 font-medium">Daily cognitive challenges · Powered by your EEG</p>
            </div>
            {/* Settings button */}
            <button
              onClick={() => setShowSettings(true)}
              className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 group"
              style={{
                background: 'rgba(34,211,238,0.08)',
                border: '1px solid rgba(34,211,238,0.2)',
              }}
              title="Quiz Settings"
            >
              <Settings className="w-4 h-4 text-[#22d3ee] transition-transform duration-300 group-hover:rotate-45" />
              {/* Active indicator dot when non-default settings */}
              {(quizSettings.difficultyMode !== 'auto' || quizSettings.calibrationBias !== 0 || quizSettings.questionCount !== 3 || quizSettings.questionTypes.length !== QUESTION_TYPE_OPTIONS.length) && (
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#F97316]" style={{ boxShadow: '0 0 4px #F97316' }} />
              )}
            </button>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)' }}
            >
              <Zap className="w-3 h-3 text-[#F97316]" />
              <span className="text-[10px] text-[#F97316] font-bold">AI-Powered</span>
            </div>
          </div>

          {/* XP bar */}
          {totalXP > 0 && (
            <div className="mb-4">
              <XPBar xp={totalXP} maxXp={150} />
            </div>
          )}

          {/* Divider */}
          <div className="h-px my-4" style={{ background: 'linear-gradient(90deg, transparent, rgba(249,115,22,0.3) 50%, transparent)' }} />

          {/* Active settings summary strip */}
          {(quizSettings.difficultyMode !== 'auto' || quizSettings.calibrationBias !== 0 || quizSettings.questionCount !== 3 || quizSettings.questionTypes.length !== QUESTION_TYPE_OPTIONS.length) && (
            <div
              className="rounded-xl px-3 py-2.5 mb-4 flex items-center gap-2 flex-wrap"
              style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}
            >
              <SlidersHorizontal className="w-3 h-3 text-[#22d3ee] flex-shrink-0" />
              <span className="text-[10px] text-[#22d3ee] font-bold uppercase tracking-wider">Custom Settings Active</span>
              <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                {quizSettings.difficultyMode !== 'auto' && (
                  <DifficultyBadge difficulty={quizSettings.difficultyMode} />
                )}
                {quizSettings.difficultyMode === 'auto' && quizSettings.calibrationBias !== 0 && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: '#22d3ee', background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
                    Bias {quizSettings.calibrationBias > 0 ? `+${quizSettings.calibrationBias}` : quizSettings.calibrationBias}
                  </span>
                )}
                {quizSettings.questionCount !== 3 && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: '#F97316', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
                    {quizSettings.questionCount}Q
                  </span>
                )}
                {quizSettings.questionTypes.length !== QUESTION_TYPE_OPTIONS.length && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: '#a855f7', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)' }}>
                    {quizSettings.questionTypes.length} types
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Brain state card */}
          {profile && metrics && (
            <div
              className="rounded-xl p-4 mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(30,58,95,0.15) 100%)',
                border: '1px solid rgba(249,115,22,0.2)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{profile.emoji}</span>
                <span className="text-[13px] font-bold text-white">Your Brain is in </span>
                <span
                  className="text-[13px] font-black px-2 py-0.5 rounded-lg"
                  style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}
                >
                  {profile.label}
                </span>
                <span className="text-[13px] font-bold text-white"> Mode</span>
              </div>
              <p className="text-[11px] text-[#8BAFD4] leading-relaxed mb-3">
                Questions will challenge your <span className="text-white font-semibold">{profile.focus}</span>.
              </p>
              <div className="flex items-center gap-3">
                {[
                  { label: 'Load', value: metrics.cognitiveLoadIndex.toFixed(0), unit: '/100' },
                  { label: 'Attention', value: metrics.attentionScore.toFixed(0), unit: '/100' },
                  { label: 'θ/α', value: metrics.thetaAlphaRatio.toFixed(2), unit: '' },
                ].map(({ label, value, unit }) => (
                  <div key={label} className="flex-1 text-center rounded-lg py-2" style={{ background: 'rgba(30,58,95,0.2)' }}>
                    <p className="text-[10px] text-[#4A7AB5] uppercase tracking-wider">{label}</p>
                    <p className="text-[14px] font-black text-white">{value}<span className="text-[10px] text-[#4A7AB5]">{unit}</span></p>
                  </div>
                ))}
                <div className="flex-1 text-center rounded-lg py-2" style={{ background: 'rgba(30,58,95,0.2)' }}>
                  <p className="text-[10px] text-[#4A7AB5] uppercase tracking-wider">Level</p>
                  <DifficultyBadge difficulty={effectiveDifficulty} />
                </div>
              </div>
            </div>
          )}

          {/* Rotating brain fact */}
          <div
            className="rounded-xl p-3 mb-5 flex items-start gap-3"
            style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}
          >
            <span className="text-xl flex-shrink-0">{BRAIN_FACTS[factIndex].emoji}</span>
            <div>
              <p className="text-[10px] text-[#22d3ee] font-bold uppercase tracking-wider mb-1">🔬 Brain Fact</p>
              <p className="text-[11px] text-[#8BAFD4] leading-relaxed" style={{ animation: 'factFade 0.5s ease-in-out' }}>
                {BRAIN_FACTS[factIndex].fact}
              </p>
            </div>
          </div>

          {/* Feature pills */}
          <div className="flex gap-2 mb-5">
            {[
              { icon: '🎯', label: `${quizSettings.questionCount} Questions` },
              { icon: '⏱️', label: '30s Timer' },
              { icon: '🏆', label: 'Earn XP' },
              { icon: '🔬', label: 'Learn Science' },
            ].map(({ icon, label }) => (
              <div
                key={label}
                className="flex-1 rounded-xl py-2 px-1 text-center"
                style={{ background: 'rgba(30,58,95,0.15)', border: '1px solid rgba(30,58,95,0.3)' }}
              >
                <p className="text-base mb-0.5">{icon}</p>
                <p className="text-[9px] text-[#8BAFD4] font-semibold">{label}</p>
              </div>
            ))}
          </div>

          {/* CTA button */}
          <button
            onClick={generateQuiz}
            className="w-full relative overflow-hidden rounded-xl py-4 text-[14px] font-black tracking-wide text-white transition-all duration-300 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #F97316 0%, #FF6B00 50%, #E55A00 100%)',
              boxShadow: '0 6px 24px rgba(249,115,22,0.4), 0 1px 0 rgba(255,255,255,0.15) inset',
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)',
                animation: 'shimmerQuiz 2.5s ease-in-out infinite',
              }}
            />
            <span className="text-lg relative z-10">🧠</span>
            <span className="relative z-10">Start Today's Challenge</span>
            <ChevronRight className="w-5 h-5 relative z-10" />
          </button>
        </div>

        <style>{`
          @keyframes shimmerQuiz {
            0% { transform: translateX(-100%); }
            60%, 100% { transform: translateX(200%); }
          }
          @keyframes accentShimmer {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
          }
          @keyframes factFade {
            from { opacity: 0; transform: translateY(4px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ── Generating State ────────────────────────────────────────────────────────
  if (phase === 'generating') {
    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(11,22,40,0.97) 0%, rgba(6,13,26,0.99) 100%)',
          border: '1px solid rgba(249,115,22,0.25)',
          boxShadow: '0 0 60px rgba(249,115,22,0.1)',
        }}
      >
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #F97316 30%, #FFD700 50%, #F97316 70%, transparent)' }} />
        <div className="p-10 flex flex-col items-center gap-6">
          {/* Animated brain */}
          <div className="relative">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'rgba(249,115,22,0.1)',
                border: '1px solid rgba(249,115,22,0.3)',
                animation: 'quizGlow 1.5s ease-in-out infinite alternate',
              }}
            >
              <span className="text-4xl" style={{ animation: 'brainBounce 1s ease-in-out infinite alternate' }}>🧠</span>
            </div>
            {/* Orbiting dots */}
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-[#F97316]"
                style={{
                  top: '50%',
                  left: '50%',
                  animation: `orbit 2s ${i * 0.67}s linear infinite`,
                  transformOrigin: '0 0',
                  marginTop: '-4px',
                  marginLeft: '-4px',
                }}
              />
            ))}
          </div>

          <div className="text-center">
            <p className="text-[17px] font-black text-white mb-2">🔬 Analyzing Your Brain State…</p>
            <p className="text-[12px] text-[#8BAFD4] max-w-xs mx-auto leading-relaxed">
              Crafting {quizSettings.questionCount} personalized cognitive challenges from your live EEG metrics
            </p>
          </div>

          {/* Loading steps */}
          <div className="w-full max-w-xs space-y-2">
            {['Reading EEG patterns…', 'Calibrating difficulty…', 'Generating questions…'].map((step, i) => (
              <div key={step} className="flex items-center gap-2" style={{ animation: `stepReveal 0.5s ${i * 0.4}s ease-out both` }}>
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{
                    background: 'rgba(34,211,238,0.2)',
                    border: '1px solid rgba(34,211,238,0.4)',
                    animation: `dotPulse 1s ${i * 0.3}s ease-in-out infinite`,
                  }}
                />
                <span className="text-[9px] text-[#4A7AB5]">{step}</span>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes quizGlow {
            from { box-shadow: 0 0 12px rgba(249,115,22,0.15); }
            to   { box-shadow: 0 0 32px rgba(249,115,22,0.45); }
          }
          @keyframes brainBounce {
            from { transform: scale(0.9) rotate(-5deg); }
            to   { transform: scale(1.1) rotate(5deg); }
          }
          @keyframes orbit {
            from { transform: rotate(0deg) translateX(44px); }
            to   { transform: rotate(360deg) translateX(44px); }
          }
          @keyframes stepReveal {
            from { opacity: 0; transform: translateX(-10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes dotPulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  // ── Active Quiz ─────────────────────────────────────────────────────────────
  if (phase === 'active' && quiz) {
    const q = quiz.questions[currentQ];
    const timerPct = timeLeft / 30;
    const timerColor = timeLeft > 15 ? '#22d3ee' : timeLeft > 7 ? '#F97316' : '#ef4444';
    const isUrgent = timeLeft <= 7;

    return (
      <div
        className="rounded-2xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(160deg, rgba(11,22,40,0.97) 0%, rgba(6,13,26,0.99) 100%)',
          border: `1px solid ${answerFlash === 'correct' ? 'rgba(34,211,238,0.5)' : answerFlash === 'wrong' ? 'rgba(239,68,68,0.5)' : 'rgba(30,58,95,0.5)'}`,
          boxShadow: answerFlash === 'correct' ? '0 0 40px rgba(34,211,238,0.15)' : answerFlash === 'wrong' ? '0 0 40px rgba(239,68,68,0.1)' : '0 0 40px rgba(249,115,22,0.04)',
          transition: 'border-color 0.3s, box-shadow 0.3s',
        }}
      >
        {/* Confetti */}
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 20 }}>
            {Array.from({ length: 16 }).map((_, i) => (
              <ConfettiParticle
                key={i}
                delay={i * 0.05}
                color={['#F97316', '#22d3ee', '#FFD700', '#a855f7', '#22c55e'][i % 5]}
              />
            ))}
          </div>
        )}

        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #F97316 30%, #FFD700 50%, #F97316 70%, transparent)' }} />

        <div className="p-5">
          {/* Top bar: progress + streak + timer */}
          <div className="flex items-center gap-3 mb-4">
            {/* Progress dots */}
            <div className="flex items-center gap-1.5 flex-1">
              {quiz.questions.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-500"
                  style={{
                    width: i === currentQ ? 28 : 10,
                    height: 8,
                    background: i < currentQ
                      ? '#22d3ee'
                      : i === currentQ
                        ? '#F97316' : 'rgba(30,58,95,0.5)',
                    boxShadow: i === currentQ ? '0 0 8px rgba(249,115,22,0.6)' : 'none',
                  }}
                />
              ))}
              <span className="text-[11px] text-[#4A7AB5] ml-1 font-semibold">{currentQ + 1}/{quiz.questions.length}</span>
            </div>

            {/* Streak badge */}
            {streak > 0 && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)' }}
              >
                <Flame className="w-3 h-3 text-[#F97316]" />
                <span className="text-[10px] text-[#F97316] font-black">{streak}x</span>
              </div>
            )}

            {/* Timer ring */}
            <div className="relative flex items-center justify-center" style={{ width: 48, height: 48 }}>
              <svg width="48" height="48" viewBox="0 0 48 48" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(30,58,95,0.4)" strokeWidth="3.5" />
                <circle
                  cx="24" cy="24" r="20" fill="none"
                  stroke={timerColor} strokeWidth="3.5"
                  strokeDasharray={`${2 * Math.PI * 20 * timerPct} ${2 * Math.PI * 20}`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 1s linear', filter: `drop-shadow(0 0 5px ${timerColor})` }}
                />
              </svg>
              <span
                className="absolute text-[13px] font-black"
                style={{ color: timerColor, animation: isUrgent ? 'urgentPulse 0.5s ease-in-out infinite alternate' : 'none' }}
              >
                {timeLeft}
              </span>
            </div>
          </div>

          {/* Category + difficulty */}
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-3.5 h-3.5 text-[#4A7AB5]" />
            <span className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-semibold">{q.category}</span>
            <span className="text-[#1E3A5F]">·</span>
            <DifficultyBadge difficulty={q.difficulty} />
          </div>

          {/* Session context (first question only) */}
          {currentQ === 0 && quiz.sessionContext && (
            <div
              className="rounded-lg px-3 py-2 mb-4 flex items-start gap-2"
              style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}
            >
              <Sparkles className="w-3.5 h-3.5 text-[#F97316] mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-[#8BAFD4] leading-relaxed">{quiz.sessionContext}</p>
            </div>
          )}

          {/* Question */}
          <div
            className="rounded-xl p-4 mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(30,58,95,0.15) 0%, rgba(30,58,95,0.08) 100%)',
              border: '1px solid rgba(30,58,95,0.35)',
            }}
          >
            <p className="text-[14px] font-bold text-white leading-relaxed">{q.question}</p>
          </div>

          {/* Feedback message */}
          {feedbackMsg && (
            <div
              className="text-center mb-3 text-[15px] font-black"
              style={{
                color: answerFlash === 'correct' ? '#22d3ee' : '#F97316',
                animation: 'feedbackPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both',
              }}
            >
              {feedbackMsg}
            </div>
          )}

          {/* Options */}
          <div className="space-y-2.5 mb-4">
            {q.options.map((opt, i) => {
              const isSelected = selectedAnswer === i;
              const isCorrect = i === q.correctIndex;

              let borderColor = 'rgba(30,58,95,0.4)';
              let bg = 'rgba(30,58,95,0.08)';
              let textColor = '#8BAFD4';
              let iconEl: React.ReactNode = null;
              let glowStyle = '';

              if (selectedAnswer !== null) {
                if (isCorrect) {
                  borderColor = 'rgba(34,211,238,0.6)';
                  bg = 'rgba(34,211,238,0.1)';
                  textColor = '#22d3ee';
                  iconEl = <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0" />;
                  glowStyle = '0 0 16px rgba(34,211,238,0.2)';
                } else if (isSelected && !isCorrect) {
                  borderColor = 'rgba(239,68,68,0.6)';
                  bg = 'rgba(239,68,68,0.1)';
                  textColor = '#ef4444';
                  iconEl = <XCircle className="w-4 h-4 text-[#ef4444] flex-shrink-0" />;
                }
              }

              const optionLetters = ['A', 'B', 'C', 'D'];

              return (
                <button
                  key={i}
                  onClick={() => handleSelectAnswer(i)}
                  disabled={selectedAnswer !== null}
                  className="w-full text-left rounded-xl px-4 py-3 transition-all duration-200 flex items-center gap-3 group"
                  style={{
                    background: bg,
                    border: `1px solid ${borderColor}`,
                    color: textColor,
                    cursor: selectedAnswer !== null ? 'default' : 'pointer',
                    boxShadow: glowStyle || 'none',
                    transform: selectedAnswer === null ? undefined : 'none',
                  }}
                >
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black flex-shrink-0 transition-all duration-200"
                    style={{
                      background: selectedAnswer === null ? 'rgba(249,115,22,0.1)' : isCorrect ? 'rgba(34,211,238,0.2)' : isSelected ? 'rgba(239,68,68,0.2)' : 'rgba(30,58,95,0.3)',
                      color: selectedAnswer === null ? '#F97316' : textColor,
                      border: `1px solid ${selectedAnswer === null ? 'rgba(249,115,22,0.2)' : borderColor}`,
                    }}
                  >
                    {optionLetters[i]}
                  </span>
                  <span className="text-[13px] font-semibold flex-1 leading-snug">{opt}</span>
                  {iconEl}
                </button>
              );
            })}
          </div>

          {/* Timed out notice */}
          {selectedAnswer === -1 && (
            <div
              className="rounded-lg px-3 py-2 mb-3 flex items-center gap-2"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
            >
              <Clock className="w-3.5 h-3.5 text-[#ef4444]" />
              <p className="text-[11px] text-[#ef4444] font-semibold">Time's up! The correct answer is highlighted above.</p>
            </div>
          )}

          {/* Explanation */}
          {showExplanation && (
            <div
              className="rounded-xl p-4 mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(30,58,95,0.1) 100%)',
                border: '1px solid rgba(249,115,22,0.25)',
                animation: 'explanationReveal 0.4s cubic-bezier(0.16,1,0.3,1) both',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">💡</span>
                <span className="text-[11px] font-black text-[#F97316] uppercase tracking-wider">Neuroscience Insight</span>
              </div>
              <p className="text-[12px] text-[#8BAFD4] leading-relaxed">{q.explanation}</p>
            </div>
          )}

          {/* Next button */}
          {showExplanation && (
            <button
              onClick={handleNext}
              className="w-full rounded-xl py-3.5 text-[13px] font-black text-white flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #F97316 0%, #E55A00 100%)',
                boxShadow: '0 4px 20px rgba(249,115,22,0.3)',
                animation: 'explanationReveal 0.4s 0.1s cubic-bezier(0.16,1,0.3,1) both',
              }}
            >
              {currentQ < quiz.questions.length - 1 ? (
                <><span>Next Question</span> <ChevronRight className="w-4 h-4" /></>
              ) : (
                <><span>🏆 View Results</span> <Trophy className="w-4 h-4" /></>
              )}
            </button>
          )}
        </div>

        <style>{`
          @keyframes explanationReveal {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes urgentPulse {
            from { transform: scale(1); }
            to   { transform: scale(1.2); }
          }
          @keyframes feedbackPop {
            from { opacity: 0; transform: scale(0.7); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes confettiFall {
            0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(200px) rotate(720deg); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // ── Complete State ──────────────────────────────────────────────────────────
  if (phase === 'complete' && quiz) {
    const pct = score / quiz.questions.length;
    const resultLabel = pct >= 0.8 ? '🏆 Excellent!' : pct >= 0.5 ? '⚡ Good Work!' : '💪 Keep Training!';
    const resultColor = pct >= 0.8 ? '#22d3ee' : pct >= 0.5 ? '#F97316' : '#ef4444';
    const resultMsg = pct >= 0.8
      ? 'Your brain is firing on all cylinders. Peak cognitive performance detected!'
      : pct >= 0.5
        ? 'Solid performance! Regular challenges strengthen neural pathways.'
        : 'Every challenge builds new neural connections. Keep training your brain!';
    const xpEarned = answers.reduce((acc, ans, i) => {
      if (ans === quiz.questions[i]?.correctIndex) {
        return acc + (quiz.questions[i].difficulty === 'hard' ? 30 : quiz.questions[i].difficulty === 'medium' ? 20 : 10);
      }
      return acc;
    }, 0);

    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(11,22,40,0.97) 0%, rgba(6,13,26,0.99) 100%)',
          border: `1px solid ${resultColor}40`,
          boxShadow: `0 0 60px ${resultColor}10`,
          animation: 'completeReveal 0.5s cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div className="h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${resultColor} 30%, #FFD700 50%, ${resultColor} 70%, transparent)` }} />

        <div className="p-6">
          {/* Score display */}
          <div className="flex flex-col items-center gap-4 mb-6">
            <ScoreRing score={score} total={quiz.questions.length} />
            <div className="text-center">
              <p className="text-[22px] font-black" style={{ color: resultColor }}>{resultLabel}</p>
              <p className="text-[12px] text-[#8BAFD4] mt-1 max-w-xs mx-auto leading-relaxed">{resultMsg}</p>
            </div>

            {/* XP earned */}
            {xpEarned > 0 && (
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(255,215,0,0.1) 100%)',
                  border: '1px solid rgba(249,115,22,0.3)',
                  animation: 'xpPop 0.6s 0.3s cubic-bezier(0.34,1.56,0.64,1) both',
                }}
              >
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-[13px] font-black text-yellow-400">+{xpEarned} XP Earned!</span>
              </div>
            )}

            {/* Streak display */}
            {score === quiz.questions.length && (
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}
              >
                <Flame className="w-4 h-4 text-[#F97316]" />
                <span className="text-[12px] font-black text-[#F97316]">Perfect Score! 🔥</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px mb-5" style={{ background: 'linear-gradient(90deg, transparent, rgba(30,58,95,0.8) 50%, transparent)' }} />

          {/* Question review */}
          <div className="space-y-3 mb-5">
            <p className="text-[11px] text-[#4A7AB5] uppercase tracking-widest font-black mb-3 flex items-center gap-2">
              <Target className="w-3.5 h-3.5" />
              Question Review
            </p>
            {quiz.questions.map((q, i) => {
              const userAns = answers[i];
              const correct = userAns === q.correctIndex;
              const timedOut = userAns === -1 || userAns === null;
              return (
                <div
                  key={i}
                  className="rounded-xl p-3"
                  style={{
                    background: correct ? 'rgba(34,211,238,0.06)' : 'rgba(239,68,68,0.06)',
                    border: `1px solid ${correct ? 'rgba(34,211,238,0.25)' : 'rgba(239,68,68,0.25)'}`,
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="text-lg flex-shrink-0">{correct ? '✅' : '❌'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-white leading-snug mb-1">{q.question}</p>
                      {!correct && (
                        <p className="text-[11px] text-[#22d3ee] font-medium">
                          ✓ Correct: {q.options[q.correctIndex]}
                        </p>
                      )}
                      {timedOut && !correct && (
                        <p className="text-[10px] text-[#4A7AB5] mt-0.5">⏰ Timed out</p>
                      )}
                    </div>
                    <DifficultyBadge difficulty={q.difficulty} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={generateQuiz}
              className="flex-1 rounded-xl py-3.5 text-[13px] font-black text-white flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #F97316 0%, #E55A00 100%)',
                boxShadow: '0 4px 20px rgba(249,115,22,0.3)',
              }}
            >
              <RefreshCw className="w-4 h-4" />
              New Challenge
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="rounded-xl px-4 py-3.5 text-[13px] font-semibold text-[#22d3ee] transition-all duration-200 flex items-center gap-1.5"
              style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)' }}
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={handleRestart}
              className="rounded-xl px-5 py-3.5 text-[13px] font-semibold text-[#8BAFD4] transition-all duration-200"
              style={{ background: 'rgba(30,58,95,0.15)', border: '1px solid rgba(30,58,95,0.4)' }}
            >
              Done
            </button>
          </div>
        </div>

        <style>{`
          @keyframes completeReveal {
            from { opacity: 0; transform: scale(0.97); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes xpPop {
            from { opacity: 0; transform: scale(0.7); }
            to   { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  return null;
}
